/* eslint-disable class-methods-use-this */
import { SearchPredicate } from '@nx-smartmonkey/shared/domain';
import { Requests } from '@nx-smartmonkey/shared/helpers';
import { PaginatedResult } from '@nx-smartmonkey/shared/interfaces';
import { FlatRoute } from '../../domain/route/FlatRoute';
import { Route } from '../../domain/route/Route';
import { Stop } from '../../domain/stop/Stop';
import { FlatRouteEntry } from '../../entries/FlatRouteEntry';
import { RouteEntry } from '../../entries/RouteEntry';
import { FlatRouteMapper } from '../../mappers/FlatRouteMapper';
import { RouteMapper } from '../../mappers/RouteMapper';
import StopMapper from '../../mappers/StopMapper';
import { SessionRepository } from '../session/SessionRepository';
import { RetrieveRoutesProps, RouteRepository, TrackPositionProps } from './RouteRepository';

export class RouteRepositoryApi implements RouteRepository {
  constructor(private requests: Requests, private sessionRepository: SessionRepository) {}

  async fetchRoute({ routeId }: { routeId: string }): Promise<Route> {
    const response = await this.requests.get({
      uri: `/drivers/v3/route/${routeId}`,
      headers: await this.sessionRepository.getAuthenticationHeader(),
    });

    return RouteMapper.toDomain(response.data);
  }

  async updateRoute({ route }: { route: Route }): Promise<Route> {
    const updateData = RouteMapper.toRepository(route);
    const response = await this.requests.put({
      uri: `/drivers/v3/route/${route.id}`,
      data: updateData,
      headers: await this.sessionRepository.getAuthenticationHeader(),
    });

    return RouteMapper.toDomain(response.data);
  }

  async deleteRoute({ route }: { route: Route }): Promise<void> {
    await this.requests.delete({
      uri: `/drivers/v3/route/${route.id}`,
      headers: await this.sessionRepository.getAuthenticationHeader(),
    });
  }

  async updateStatus({ route }: { route: Route }): Promise<Route> {
    await this.requests.put({
      uri: `/drivers/v3/route/${route.id}/status`,
      data: {
        status: route.status,
      },
      headers: await this.sessionRepository.getAuthenticationHeader(),
    });

    // Devolvemos la ruta actualizada, pero no la del request. Esto es por que el request devuelve FlatRoute y el código funciona con Route.
    // De esta manera nos evitamos tener que hacer todo el modelo de FlatRoute.
    // El unico problema es que no se actualiza el estado de la ruta en el contexto con la nueva información si hubiera.

    //route end
    if (route.start_date && route.end_date) {
      if (route.status === `not_started`) {
        route.start_date = undefined;
        route.end_date = undefined;
      } else if (route.status === `in_transit`) {
        route.end_date = undefined;
      }
    }
    //route in_transit
    else if (route.start_date && !route.end_date) {
      if (route.status === `not_started`) {
        route.start_date = undefined;
      } else if (route.status === `finished`) {
        route.end_date = new Date();
      }
    }
    //route  not_started
    else if (!route.start_date && !route.end_date) {
      if (route.status === `in_transit`) {
        route.start_date = new Date();
      } else if (route.status === `finished`) {
        route.start_date = new Date();
        route.end_date = new Date();
      }
    }

    return route;
  }

  async updateStopsOrder({
    stops,
    optimize,
    route,
  }: {
    stops: string[];
    optimize: boolean;
    route: Route;
  }): Promise<Route> {
    const response = await this.requests.put({
      uri: `/drivers/v3/route/${route.id}/reorder`,
      data: {
        first_stops: stops,
        optimize: optimize,
      },
      headers: await this.sessionRepository.getAuthenticationHeader(),
    });

    const updatedRoute = RouteMapper.toDomain(response.data);
    return updatedRoute;
  }

  async fetchRoutes({ offset, limit, sort, status }: RetrieveRoutesProps): Promise<PaginatedResult<Route>> {
    const params = new URLSearchParams();

    if (offset) params.append(`offset`, `${offset}`);
    if (limit) params.append(`limit`, `${limit}`);
    if (sort) params.append(`sort`, sort);
    status.forEach((st) => params.append(`status`, st));

    const response = await this.requests.get({
      uri: `/drivers/v3/vehicle/routes?${params.toString()}`,
      headers: await this.sessionRepository.getAuthenticationHeader(),
    });

    const docs = (response.data.docs ?? []).map((route: RouteEntry) => RouteMapper.toDomain(route));

    return { ...response.data, docs };
  }

  async trackPosition({ route, lat, lng, source }: TrackPositionProps): Promise<void> {
    await this.requests.post({
      uri: `/drivers/v3/route/${route.id}/track`,
      data: {
        lat,
        lng,
        source,
      },
      headers: await this.sessionRepository.getAuthenticationHeader(),
    });
  }

  async searchRoutes({
    offset,
    limit,
    sort_by,
    sort_direction,
    predicates,
  }: {
    offset?: number;
    limit?: number;
    sort_by?: string;
    sort_direction?: string;
    predicates: Array<SearchPredicate>;
  }): Promise<PaginatedResult<Route>> {
    const response = await this.requests.post({
      uri: `/drivers/v3/vehicle/routes/search`,
      data: {
        offset,
        limit,
        predicates: predicates.map((p) => p.getValue()),
        ...(sort_by !== undefined ? { sort_by } : {}),
        ...(sort_direction !== undefined ? { sort_direction } : {}),
      },
      headers: await this.sessionRepository.getAuthenticationHeader(),
    });

    const docs = (response.data.docs ?? []).map((route: RouteEntry) => RouteMapper.toDomain(route));

    return { ...response.data, docs };
  }
  async searchFlatRoutes({
    offset,
    limit,
    sort_by,
    sort_direction,
    predicates,
  }: {
    offset?: number;
    limit?: number;
    sort_by?: string;
    sort_direction?: string;
    predicates: Array<SearchPredicate>;
  }): Promise<PaginatedResult<FlatRoute>> {
    const response = await this.requests.post({
      uri: `/drivers/v3/vehicle/routes/search/flat`,
      data: {
        offset,
        limit,
        predicates: predicates.map((p) => p.getValue()),
        ...(sort_by !== undefined ? { sort_by } : {}),
        ...(sort_direction !== undefined ? { sort_direction } : {}),
      },
      headers: await this.sessionRepository.getAuthenticationHeader(),
    });

    const docs = (response.data.docs ?? []).map((route: FlatRouteEntry) => FlatRouteMapper.toDomain(route));

    return { ...response.data, docs };
  }

  async searchUnfinishedRoutes(): Promise<number> {
    const response = await this.requests.get({
      uri: `/drivers/v3/vehicle/routes/search/unfinished`,
      headers: await this.sessionRepository.getAuthenticationHeader(),
    });
    return response.data;
  }

  async endUnfinishedRoutes(): Promise<number> {
    const response = await this.requests.put({
      uri: `/drivers/v3/vehicle/routes/search/unfinished`,
      headers: await this.sessionRepository.getAuthenticationHeader(),
    });
    return response.data;
  }

  async acceptOptimization({ route_id, log_id }: { route_id: string; log_id: string }): Promise<Route> {
    const response = await this.requests.put({
      uri: `/drivers/v3/route/${route_id}/accept/${log_id}`,
      headers: await this.sessionRepository.getAuthenticationHeader(),
    });
    const updatedRoute = RouteMapper.toDomain(response.data);
    return updatedRoute;
  }

  async createStops({ stops, route, optimize }: { stops: Stop[]; route: Route; optimize: boolean }): Promise<Route> {
    const response = await this.requests.post({
      uri: `/drivers/v3/route/${route.id}/stops`,
      data: {
        stops: stops.map((stop) => StopMapper.toRepository(stop)),
        optimize,
      },
      headers: {
        ...(await this.sessionRepository.getAuthenticationHeader()),
      },
    });

    return RouteMapper.toDomain(response.data);
  }

  async createRoute({
    label,
    execution_date,
    stops_ids_to_move,
  }: {
    label?: string;
    execution_date: Date;
    stops_ids_to_move?: string[];
  }): Promise<Route> {
    const response = await this.requests.post({
      uri: `/drivers/v3/route`,
      data: {
        label,
        execution_date,
        stops_ids_to_move,
      },
      headers: {
        ...(await this.sessionRepository.getAuthenticationHeader()),
      },
    });

    return RouteMapper.toDomain(response.data);
  }
  async duplicateRoute({
    label,
    execution_date,
    route_id,
  }: {
    label: string;
    execution_date: Date;
    route_id: string;
  }): Promise<Route> {
    const response = await this.requests.post({
      uri: `/drivers/v3/route/${route_id}/duplicate`,
      data: {
        label,
        execution_date,
      },
      headers: {
        ...(await this.sessionRepository.getAuthenticationHeader()),
      },
    });

    return RouteMapper.toDomain(response.data);
  }
}
